import * as React from 'react';
import chroma from 'chroma-js';
import { styles } from './styles';
import { JoyLogo } from './components/JoyLogo';
import { buildReactStateHandler, makeDebugger, isEqual } from '@lib';
import MediaQuery from 'react-responsive';
import {
  DesignSection,
  ThemeDesignConfig,
  LayoutDesignConfig,
  ColorDesignConfig,
  Device,
  ThemeDesignHierarchy,
  FontDesignConfig,
  DesignSectionConfig,
  StyleBucket,
  DemoFontOptionKey,
  LocationPalette
} from './types';
import { Layout } from '@graphql/types';
import { LayoutDesign } from './components/designOptions/LayoutDesign';
import { Preview, PreviewProps } from '@routes/event/guest/NewPreview';
import { ThemeDesign } from './components/designOptions/ThemeDesign';
import { DeviceToggle } from './components/DeviceToggle';

import { FontDesign } from './components/designOptions/FontDesign';
import { ColorDesign } from './components/designOptions/ColorDesign';
import { Advanced } from './components/designOptions/Advanced';
import { PREVIEW_FONTS } from './constants';
import { hydrateStateFromSearch } from './util/hydrateStateFromSearch';
import {
  PreviewMessage,
  PreviewProperty,
  PreviewInitializationSource,
  PreviewKeyValue
} from '@routes/event/guest/Template/services/previewListener/types';
import { BaseButton } from '../components/BaseButton';
import {
  DesignDemo_designShowcase_organization_allStyles,
  DesignShowcase_designShowcase_organization_colors_buckets,
  DesignShowcaseOrganizationBucket
} from '@graphql/designShowcaseTypes';
import { api } from '../DesignShowcase/modules/api';
import { Spinner } from '@components/Spinner';
import memoizeOne from 'memoize-one';
import { telemetry } from '@services/tracking/telemetry';
import { generateCreateEventQueryString } from './util/generateCreateEventQueryString';
import { isValidThemeForLayout, themeService } from '@routes/event/guest/services/theme/themeService';
import { breakpoints } from '@joykit/variables';
import { handlers } from './handlers';
import { HeadSection } from './components/HeadSection';
import { JoyHeader } from '@components/JoyHeader';
import { ErrorPage } from '@components/Error';
import { ApolloError } from 'apollo-client';
import { CreateWeddingPopup } from './components/CreateWeddingPopup';
import { RouteComponentProps } from 'react-router';
import { getMarketingEnv } from 'utils/getJoyEnv';

const stateUpdater = buildReactStateHandler<DesignDemoState>();
const debug = makeDebugger('<DesignDemo />');
const DEFAULT_THEME = 'botanical_geo';

interface Props {
  history: RouteComponentProps['history'];
}

export type DesignDemoState = Readonly<{
  hasLoaded: boolean;
  hasError: undefined | ApolloError;
  hasIframeError: undefined | ApolloError;
  showMobileNav: boolean;
  device: Device;
  sections: {
    themes: ThemeDesignConfig;
    layout: LayoutDesignConfig;
    colors: ColorDesignConfig;
    fonts: FontDesignConfig;
    advanced: DesignSectionConfig;
  };
  hasLoadedPaletteFromSearch: boolean;
  palette: LocationPalette;
  showCreateWeddingModal: boolean;
  assetId: string;
  owner: string;
  fiancee: string;
  greeting: string;
}>;

export type StyleByIdMap = {
  [styleKey: string]: DesignDemo_designShowcase_organization_allStyles;
};

class DesignDemo extends React.PureComponent<Props, DesignDemoState> {
  readonly state = hydrateStateFromSearch();

  private previewRef: React.RefObject<Preview> = React.createRef();
  private navRef: React.RefObject<HTMLDivElement> = React.createRef();

  private styleData: DesignDemo_designShowcase_organization_allStyles[];

  private styleBuckets: StyleBucket[];
  private styleByIdMap: StyleByIdMap;
  private colorBuckets: DesignShowcase_designShowcase_organization_colors_buckets[];
  // ----------------------------------------
  // Lifecycle
  // ----------------------------------------

  componentDidMount() {
    window.WebFont.load({
      google: {
        families: PREVIEW_FONTS.map(font => font.fontFamily)
      }
    });

    window.onIframeError = apolloError => {
      this.setState(
        stateUpdater(draft => {
          draft.hasIframeError = apolloError;
        })
      );
    };

    this.requestThemeData();
    telemetry.sendEvent({
      type: 'page',
      name: 'marketing.preview'
    });
  }

  componentWillUnmount() {
    // so IE doesn't blow up
    window['onIframeError'] = undefined;
    try {
      delete window['onIframeError'];
    } catch (e) {
      // do nothing
    }
  }

  requestThemeData = async () => {
    try {
      const response = await api.requestDemoThemes();
      if (response && response.designShowcase) {
        this.initializeStyleData(response.designShowcase.organization.allStyles);
        this.colorBuckets = response.designShowcase.organization.colors.buckets;
        this.setState(hydrateStateFromSearch(true, this.styleByIdMap));
      }
    } catch (e) {
      this.setState(
        stateUpdater(draft => {
          draft.hasError = e;
        })
      );
    }
  };

  initializeStyleData = (styleData: DesignDemo_designShowcase_organization_allStyles[]) => {
    this.styleData = styleData;
    const styleByIdMap: StyleByIdMap = {};
    (window as any).style = styleData;
    this.styleData.forEach(style => {
      styleByIdMap[style.id] = style;
    });
    this.styleByIdMap = styleByIdMap;
    this.getStyleBuckets(this.state.sections.layout.layout);
  };

  // ----------------------------------------
  // Callback handlers
  // ----------------------------------------

  handleOnPreviewInitialized: PreviewProps['onInitialized'] = event => {
    if (!!location.search) {
      // Update Preview from query string
      this.passMessageToPreview(PreviewInitializationSource.searchParams, [
        { key: 'template', value: this.state.sections.layout.layout },
        { key: 'theme', value: this.state.sections.themes.themeKey },
        { key: 'color', value: this.state.sections.colors.hex },
        { key: 'font', value: this.state.sections.fonts.font },
        { key: 'assetId', value: this.state.assetId },
        { key: 'ownerFirstName', value: this.state.owner },
        { key: 'fianceeFirstName', value: this.state.fiancee },
        { key: 'greeting', value: this.state.greeting }
      ]);
    } else {
      debug(`Hydrating demo state from <Preview /> target event`);
      // Hydrate state from the loaded event in the preview
      let previewUpdates: PreviewKeyValue[] = [];

      this.setState(
        stateUpdater(draft => {
          // Layout
          draft.sections.layout.layout = event.style.layout;

          // Color
          draft.sections.colors.hex = event.style.primaryColor;
          if (event.style.suggestedColor) {
            draft.sections.colors.displayString = 'Custom Color';
          }
          // Font
          // TODO: Once evan's font work is in, we can validate the event font against the curated selection of fonts
          draft.sections.fonts.font = event.style.font as DemoFontOptionKey;

          // Theme
          const selectedStyle = this.styleByIdMap[event.style.theme];
          if (selectedStyle) {
            draft.sections.themes.themeKey = selectedStyle.id;
            draft.sections.themes.category = selectedStyle.organizationBuckets[0].bucketId;

            if (selectedStyle.id === 'blank') {
              draft.sections.themes.useSuggestedBackgroundColor = false;
              previewUpdates.push({ key: 'suggestedColor', value: false });
            } else {
              draft.sections.themes.useSuggestedBackgroundColor = true;
              previewUpdates.push({ key: 'suggestedColor', value: true });
            }
          } else {
            previewUpdates.push({ key: 'theme', value: DEFAULT_THEME });
            debug(`API data could not resolve the provided event theme: ${event.style.theme}`);
          }
        }),
        () => {
          let action = PreviewInitializationSource.onInitialized;
          if (previewUpdates.length) {
            this.passMessageToPreview(action, previewUpdates);
          }
        }
      );
    }
  };

  handleMainClick: React.MouseEventHandler<HTMLMainElement> = e => {
    if (
      this.state.showMobileNav &&
      this.navRef.current &&
      e.target instanceof HTMLElement &&
      !this.navRef.current.contains(e.target)
    ) {
      this.setState(
        stateUpdater(draft => {
          draft.showMobileNav = false;
        })
      );
    }
  };

  handleStartWithDesignClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    const { layout, themes, colors, fonts } = this.state.sections;
    const buttonId = e.currentTarget.id ? e.currentTarget.id : 'unknown';
    telemetry.sendEvent({
      category: 'marketing.preview',
      action: 'StartCreate',
      label: buttonId,
      extraInfo: { layout: layout.layout, theme: themes.themeKey, baseColor: colors.hex, fontName: fonts.font } // FUTURE add primaryPhotoHorizontal: assetId when passed from /designs
    });
    const queryString = generateCreateEventQueryString(this.state.sections, this.state.palette, this.styleByIdMap);
    const createUrl = queryString ? `/createwedding?${queryString}` : '/createwedding';
    const url = `${getMarketingEnv(window.joyEnv)}${createUrl}`;
    window.location.href = url;
  };

  handleDeviceSelect: React.MouseEventHandler<HTMLButtonElement> = e => {
    const device = e.currentTarget.value;
    this.setState(
      stateUpdater(draft => {
        draft.device = device as Device;
      })
    );
    telemetry.sendEvent({
      category: 'marketing.preview',
      action: 'TogglePreview',
      label: device
    });
  };

  handleSectionToggle: React.MouseEventHandler<HTMLButtonElement> = e => {
    const section = e.currentTarget.value as DesignSection;
    this.setState(
      stateUpdater(draft => {
        const invereSectionVisibility = !draft.sections[section].open;
        draft.sections[section].open = invereSectionVisibility;
        if (section === 'themes' && invereSectionVisibility && draft.sections.themes.category) {
          draft.sections.themes.showCategoryBuckets = true;
        }
        if (invereSectionVisibility) {
          // is open, close other sections
          Object.keys(draft.sections).forEach(targetSection => {
            if (
              targetSection !== section &&
              draft.sections[targetSection] &&
              draft.sections[targetSection].open !== undefined
            ) {
              draft.sections[targetSection].open = false;
            }
          });
        }
        if (draft.sections[section].open) {
          telemetry.sendEvent({
            category: 'marketing.preview',
            action: 'ExpandPanel',
            label: section
          });
        }
      })
    );
  };

  handleOnSelect: React.MouseEventHandler<HTMLButtonElement> = e => {
    const section = e.currentTarget.dataset.section as DesignSection;
    if (!section) {
      throw new Error('Section data attribute has not been supplied');
    }

    switch (section) {
      case 'themes':
        this.handleThemeSelect(e);
        break;
      case 'layout':
        this.handleLayoutSelect(e);
        break;
      case 'fonts':
        this.handleSelection('font', 'fonts', 'font', e.currentTarget.value);
        break;
      case 'colors':
        this.handleColorSelect(e.currentTarget.value, e.currentTarget.dataset.displaystring!);
        break;
      default:
        break;
    }
  };

  handleThemeBackClick = () => {
    this.setState(
      stateUpdater(draft => {
        // TODO: find correct category bucket
        draft.sections.themes.showCategoryBuckets = false;
      })
    );
  };

  handleUseWeddingColor: React.MouseEventHandler<HTMLInputElement> = e => {
    this.setState(
      stateUpdater(draft => {
        draft.sections.themes.useSuggestedBackgroundColor = !draft.sections.themes.useSuggestedBackgroundColor;
      }),
      () => {
        telemetry.sendEvent({
          category: 'marketing.preview',
          action: 'UseSuggestedColor',
          label: this.state.sections.themes.useSuggestedBackgroundColor
        });
        this.passMessageToPreview('suggestedColor', this.state.sections.themes.useSuggestedBackgroundColor);
      }
    );
  };

  handleCreateWeddingClose = () => this.setState(handlers.handleCreateWeddingClose());

  handleCreateWeddingClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    const { layout, themes, colors, fonts } = this.state.sections;
    const buttonId = e.currentTarget.id ? e.currentTarget.id : 'unknown';
    telemetry.sendEvent({
      category: 'marketing.preview',
      action: 'StartCreate',
      label: buttonId,
      extraInfo: { layout: layout.layout, theme: themes.themeKey, baseColor: colors.hex, fontName: fonts.font }
    });
    this.setState(handlers.handleCreateWeddingClick());
  };

  // ----------------------------------------
  // Render
  // ----------------------------------------

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorPage
          page="landing"
          pagePrefix="marketing"
          category="Preview"
          attemptedAction="Request Demo Themes"
          error={this.state.hasError}
        />
      );
    }
    if (this.state.hasIframeError) {
      return (
        <ErrorPage
          page="landing"
          pagePrefix="marketing"
          category="Preview"
          attemptedAction="Load iframe"
          error={this.state.hasIframeError}
        />
      );
    }
    if (!this.state.hasLoaded) {
      return (
        <div style={{ margin: '40px' }}>
          <JoyHeader />
          <Spinner style={{ top: '20%', height: '50%' }} />
        </div>
      );
    }
    const {
      sections: { layout, themes, fonts, colors, advanced },
      device,
      showCreateWeddingModal
    } = this.state;
    const styleBuckets = this.getStyleBuckets(layout.layout);
    const selectedStyle = this.getStyleByKey(themes.themeKey, styleBuckets);
    return (
      <>
        <HeadSection />
        <MediaQuery maxWidth={breakpoints.medium}>
          {match => {
            return (
              <div onClick={this.handleMainClick} className={styles.container}>
                <div
                  ref={this.navRef}
                  className={styles.designPanelWrapper(match, match ? this.state.showMobileNav : true, colors.hex)}
                >
                  <div className={styles.designPanelInner}>
                    <div className={styles.logo}>
                      {
                        <a aria-label="Go to withjoy.com" href="https://withjoy.com/">
                          <JoyLogo />
                        </a>
                      }
                    </div>
                    <div>
                      <LayoutDesign
                        onSectionToggle={this.handleSectionToggle}
                        onSelect={this.handleOnSelect}
                        open={layout.open}
                        layout={layout.layout}
                      />
                      <ThemeDesign
                        open={themes.open}
                        onSectionToggle={this.handleSectionToggle}
                        showCategoryBuckets={this.state.sections.themes.showCategoryBuckets}
                        onBackClick={this.handleThemeBackClick}
                        onSelect={this.handleOnSelect}
                        category={themes.category}
                        theme={themes.themeKey}
                        selectedStyle={selectedStyle}
                        styleBuckets={styleBuckets}
                      />
                      <ColorDesign
                        open={colors.open}
                        color={colors.hex}
                        displayString={colors.displayString}
                        currentLayout={layout.layout}
                        currentThemeKey={themes.themeKey}
                        useSuggestedBackgroundColor={this.state.sections.themes.useSuggestedBackgroundColor}
                        colors={this.getColors(this.state.hasLoadedPaletteFromSearch, this.state.palette.baseColor)}
                        onSectionToggle={this.handleSectionToggle}
                        onColorSelect={this.handleOnSelect}
                        onStartClick={this.handleStartWithDesignClick}
                        onUseWeddingColorClick={this.handleUseWeddingColor}
                      />
                      <FontDesign
                        open={fonts.open}
                        font={fonts.font}
                        customFont={fonts.custom}
                        onSectionToggle={this.handleSectionToggle}
                        onStartClick={this.handleStartWithDesignClick}
                        onFontSelect={this.handleOnSelect}
                      />
                      <Advanced open={advanced.open} onSectionToggle={this.handleSectionToggle} />
                    </div>
                  </div>
                </div>

                <div aria-hidden="true" className={styles.previewWrapper(colors.hex, match)}>
                  <DeviceToggle
                    color={colors.hex}
                    device={device}
                    mobile={match}
                    handleFilterButtonClick={() => this.setState({ showMobileNav: true })}
                    handleDeviceSelect={this.handleDeviceSelect}
                  />
                  <div data-preview-content="" className={styles.previewContent}>
                    <Preview
                      onInitialized={this.handleOnPreviewInitialized}
                      device={device}
                      ref={this.previewRef}
                      url={`/rosie-collins-and-alex-claflin`}
                    />
                  </div>
                  <div className={styles.startButtonContainer}>
                    <BaseButton
                      id="main"
                      className={styles.startButton(colors.hex)}
                      onClick={this.handleCreateWeddingClick}
                    >
                      <span>Start With This Design</span>
                    </BaseButton>
                  </div>
                </div>
                <CreateWeddingPopup
                  showCreateWeddingModal={showCreateWeddingModal}
                  handleCreateWeddingClose={this.handleCreateWeddingClose}
                  designDemoState={this.state}
                />
              </div>
            );
          }}
        </MediaQuery>
      </>
    );
  }

  // ----------------------------------------
  // Helpers
  // ----------------------------------------

  private matchesPalette = (hexString: string) =>
    this.colorBuckets.find((color: DesignShowcase_designShowcase_organization_colors_buckets) => {
      if (color.displayRGBAColor) {
        return color.displayRGBAColor.hexString === `${hexString.substr(1)}ff`;
      }
      return false;
    });

  private getColors = memoizeOne(
    (
      hasLoadedPaletteFromSearch: boolean,
      paletteBaseColor: number[]
    ): DesignShowcase_designShowcase_organization_colors_buckets[] => {
      if (hasLoadedPaletteFromSearch) {
        const hexString = chroma([...paletteBaseColor.slice(0, 3).map(x => x * 255), paletteBaseColor[3]]).hex();
        const match = this.matchesPalette(hexString);
        if (match) {
          return this.colorBuckets;
        }
        const customColor: DesignShowcaseOrganizationBucket = {
          __typename: 'DesignShowcaseOrganizationBucket',
          displayRGBAColor: {
            __typename: 'DesignShowcaseRGBAColor',
            hexString,
            floatArray: []
          },
          displayString: 'Custom Color',
          bucketId: 'customColor',
          bucketGroupId: 'colors'
        };
        return [customColor, ...this.colorBuckets];
      }
      return this.colorBuckets;
    },
    isEqual
  );

  private getStyleByKey = (
    styleKey: string,
    styleBuckets: StyleBucket[],
    defaultStyle?: DesignDemo_designShowcase_organization_allStyles
  ) => {
    debug('getStyleByKey()');
    const targetStyle = this.styleData.find(style => style.id === styleKey);
    if (targetStyle) {
      return targetStyle;
    }

    defaultStyle = defaultStyle || styleBuckets[0].styles[0];
    debug(`Style not found for key: ${styleKey}. Defaulting to: ${defaultStyle.id} `);
    return defaultStyle;
  };

  /**
   * Given a layout, we will receive the same set of themes every time.
   */
  private getStyleBuckets = memoizeOne((layout: Layout): StyleBucket[] => {
    debug(`getStyleBuckets()`);
    /**
     * Get styles by layout
     */
    const stylesByLayout: DesignDemo_designShowcase_organization_allStyles[] = [];
    const isAlohaLayout = layout === Layout.aloha;
    const isBrannanLayout = layout === Layout.brannan;
    this.styleData.forEach(style => {
      if (
        (isAlohaLayout && style.supportsWebsiteLayoutAloha) ||
        (isBrannanLayout && style.supportsWebsiteLayoutBrannan)
      ) {
        // Since there can only be one layout selection at a given moment,
        // this bool check is effectively an XOR conditional
        stylesByLayout.push(style);
      } else if (!style.supportsWebsiteLayoutAloha && !style.supportsWebsiteLayoutBrannan) {
        // TODO: Would be a good idea to telemetry this or something.
        debug(`Style - ${style.id} - not supported by: [aloha, brannan] - (all)`);
      }
    });

    /**
     * Get list of bucketId's that are for non-empty bucket, given the specified layout
     */
    const unsortedStyleBucketsByLayout: { [styleId: string]: StyleBucket } = {};
    const TARGET_ORG_BUCKET_GROUP_ID = 'themes';
    stylesByLayout.forEach(style => {
      style.organizationBuckets.forEach(orgBucket => {
        // This layout specific style may belong to multiple organization buckets
        if (orgBucket.bucketGroupId === TARGET_ORG_BUCKET_GROUP_ID) {
          // Only considering theme bucket groups
          const styleBucket = unsortedStyleBucketsByLayout[orgBucket.bucketId];
          if (styleBucket) {
            // Style bucket exists, so add this current style to the list
            styleBucket.styles.push(style);
          } else {
            // Bucket is new, create a new StyleBucket and set this style as the first item
            unsortedStyleBucketsByLayout[orgBucket.bucketId] = {
              organizationBucket: orgBucket,
              styles: [style]
            };
          }
        }
      });
    });

    const styleBuckets: StyleBucket[] = [];

    Object.keys(unsortedStyleBucketsByLayout)
      .sort()
      .map(bucketId => {
        styleBuckets.push(unsortedStyleBucketsByLayout[bucketId]);
      });

    this.styleBuckets = styleBuckets;
    return this.styleBuckets;
  }, isEqual);

  private passMessageToPreview = (action: PreviewMessage['action'], value: PreviewMessage['value']) => {
    if (this.previewRef.current) {
      this.previewRef.current.sendMessage({ action, value });
    }
  };

  private handleThemeSelect: React.MouseEventHandler<HTMLButtonElement> = e => {
    const value = e.currentTarget.value;
    const type = e.currentTarget.dataset.type as ThemeDesignHierarchy;
    const currentLayout = this.state.sections.layout.layout;
    if (type === 'bucketGroup') {
      this.setState(
        stateUpdater(draft => {
          draft.sections.themes.showCategoryBuckets = true;
          draft.sections.themes.category = value;
        })
      );
      telemetry.sendEvent({
        category: 'marketing.preview',
        action: `ExpandThemeCategory`,
        label: value
      });
    } else {
      if (value !== this.state.sections.themes.themeKey) {
        if (isValidThemeForLayout(value, currentLayout)) {
          this.setState(
            stateUpdater(draft => {
              draft.sections.themes.themeKey = value;
              draft.sections.themes.useSuggestedBackgroundColor = true;
            })
          );
          this.passMessageToPreview('fromList', [{ key: 'theme', value }, { key: 'suggestedColor', value: true }]);
          telemetry.sendEvent({
            category: 'marketing.preview',
            action: `TriedTheme`,
            label: value
          });
        } else {
          debug('whoa wtf NOT GOOD');
        }
      }
    }
  };
  private handleLayoutSelect: React.MouseEventHandler<HTMLButtonElement> = e => {
    const layout = e.currentTarget.value as Layout;
    if (layout !== this.state.sections.layout.layout) {
      const isBrannan = layout === Layout.brannan;
      this.setState(
        stateUpdater(draft => {
          draft.sections.layout.layout = layout as Layout;
          if (!isBrannan) {
            draft.sections.themes.useSuggestedBackgroundColor = false;
          }
          if (isBrannan && draft.sections.themes.themeKey !== 'blank') {
            const targetStyle = this.styleData.find(s => s.id === draft.sections.themes.themeKey);
            if (!targetStyle || !targetStyle.supportsWebsiteLayoutBrannan) {
              // Theme not found | brannan cannot handle
              draft.sections.themes.themeKey = 'blank';
            }
          }
        })
      );
      this.passMessageToPreview('layout', layout);
      telemetry.sendEvent({
        category: 'marketing.preview',
        action: `SelectLayout`,
        label: layout
      });
    }
  };

  private handleColorSelect = (hex: string, displayString: string) => {
    if (hex !== this.state.sections['colors'].hex) {
      const supportsWeddingColor = themeService(this.state.sections.themes.themeKey, Layout.aloha).supportsWeddingColor;

      this.setState(
        stateUpdater(draft => {
          draft.sections['colors'].hex = hex;
          draft.sections['colors'].displayString = displayString;
          draft.sections.themes.useSuggestedBackgroundColor = false;
        })
      );

      // if supports wedding color, useSuggestedBackground color is false, otherwise true
      this.passMessageToPreview('fromList', [
        { key: 'suggestedColor', value: supportsWeddingColor ? false : true },
        { key: 'color', value: hex }
      ]);
      telemetry.sendEvent({
        category: 'marketing.preview',
        action: 'Select Colors',
        label: hex
      });
    }
  };

  private handleSelection = (
    previewProperty: PreviewProperty,
    section: keyof Pick<DesignDemoState['sections'], 'fonts' | 'layout' | 'colors'>,
    field: string,
    update: string,
    displayString?: string
  ) => {
    if (update !== this.state.sections[section][field]) {
      this.setState(
        stateUpdater(draft => {
          draft.sections[section][field] = update;
        })
      );
      this.passMessageToPreview(previewProperty, update);
      const telemetryAction = section.charAt(0).toUpperCase() + section.slice(1, section.length - 1);
      telemetry.sendEvent({
        // fonts
        category: 'marketing.preview',
        action: `Select${telemetryAction}`,
        label: update
      });
    }
  };
}

export { DesignDemo };
